import React, { useEffect, useRef, useState } from "react";
import { AxisMove, AxisSector, PuzzleState } from "../../game/types";

interface PuzzleGridViewProps {
  state: PuzzleState;
  isShowingSolution: boolean;
  completedAt: number | null;
  onAxisMove(move: AxisMove): void;
  onAxisNudge(delta: { dVertical?: number; dHorizontal?: number }): void;
}

export const PuzzleGridView: React.FC<PuzzleGridViewProps> = ({
  state,
  isShowingSolution,
  completedAt,
  onAxisMove,
  onAxisNudge
}) => {
  const { grid, dimensions, axes } = state;
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [isDraggingAxes, setIsDraggingAxes] = useState(false);
  const swipeStartRef = useRef<{
    x: number;
    y: number;
    nx: number;
    ny: number;
  } | null>(null);
  const size = dimensions.rows;
  // Test image from Unsplash: "black wooden window on brown brick wall" by Christopher Ott
  // Page: https://unsplash.com/photos/black-wooden-window-on-brown-brick-wall-gt3qT1h86bM
  // Direct download URL (resolves to the actual JPEG):
  const baseUrl =
    "https://unsplash.com/photos/gt3qT1h86bM/download?force=true&w=800";

  const pieceStyle: React.CSSProperties = {
    width: "100%",
    aspectRatio: "1 / 1",
    backgroundImage: `url(${baseUrl})`,
    backgroundSize: `${size * 100}% ${size * 100}%`,
    backgroundRepeat: "no-repeat",
    borderRadius: 2,
    border: "1px solid rgba(15, 23, 42, 0.75)"
  };

  const solvedGrid: number[][] = [];
  {
    let id = 0;
    for (let r = 0; r < dimensions.rows; r++) {
      const row: number[] = [];
      for (let c = 0; c < dimensions.cols; c++) {
        row.push(id++);
      }
      solvedGrid.push(row);
    }
  }

  const positionInBoard = (clientX: number, clientY: number) => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ratioX = clamp(x / rect.width, 0, 1);
    const ratioY = clamp(y / rect.height, 0, 1);
    return { ratioX, ratioY };
  };

  const updateAxesFromPointer = (clientX: number, clientY: number) => {
    const pos = positionInBoard(clientX, clientY);
    if (!pos) return;
    const { ratioX, ratioY } = pos;
    const nextVertical = clamp(Math.round(ratioX * dimensions.cols), 0, dimensions.cols);
    const nextHorizontal = clamp(Math.round(ratioY * dimensions.rows), 0, dimensions.rows);
    const dV = nextVertical - axes.verticalIndex;
    const dH = nextHorizontal - axes.horizontalIndex;
    if (dV !== 0 || dH !== 0) {
      onAxisNudge({ dVertical: dV, dHorizontal: dH });
    }
  };

  useEffect(() => {
    if (!isDraggingAxes) return;
    const handleMove = (e: PointerEvent) => {
      e.preventDefault();
      updateAxesFromPointer(e.clientX, e.clientY);
    };
    const handleUp = () => setIsDraggingAxes(false);
    window.addEventListener("pointermove", handleMove, { passive: false });
    window.addEventListener("pointerup", handleUp, { passive: true });
    window.addEventListener("pointercancel", handleUp, { passive: true });
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [isDraggingAxes, axes.verticalIndex, axes.horizontalIndex, dimensions.cols, dimensions.rows]);

  useEffect(() => {
    const handleUp = (e: PointerEvent) => {
      const start = swipeStartRef.current;
      if (!start) return;
      swipeStartRef.current = null;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const distanceSquared = dx * dx + dy * dy;
      const tapThresholdSq = 25;

      // Very small movement: treat as tap → reposition axes.
      if (distanceSquared < tapThresholdSq) {
        updateAxesFromPointer(start.x, start.y);
        return;
      }

      const axisNX = axes.verticalIndex / dimensions.cols;
      const axisNY = axes.horizontalIndex / dimensions.rows;
      const maxDistanceFromAxis = 0.25;
      const distToVertical = Math.abs(start.nx - axisNX);
      const distToHorizontal = Math.abs(start.ny - axisNY);

      // Must be near at least one axis to count as a swap gesture.
      if (distToVertical > maxDistanceFromAxis && distToHorizontal > maxDistanceFromAxis) {
        return;
      }

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const minSwipe = 15;

      // Determine swipe direction first, then check proximity to relevant axis.
      // Horizontal swipe (dx dominant) → swap across vertical axis
      // Vertical swipe (dy dominant) → swap across horizontal axis
      const isHorizontalSwipe = absDx >= absDy * 0.6 && absDx >= minSwipe;
      const isVerticalSwipe = absDy >= absDx * 0.6 && absDy >= minSwipe;

      if (isHorizontalSwipe && distToVertical <= maxDistanceFromAxis) {
        // Horizontal swipe near vertical axis → swap across vertical axis
        // Check if vertical axis is not on left/right border.
        if (axes.verticalIndex > 0 && axes.verticalIndex < dimensions.cols) {
          let sector: AxisSector;
          if (axes.horizontalIndex === 0) {
            // Horizontal axis on top border: only "positive" sector exists.
            sector = "positive";
          } else if (axes.horizontalIndex === dimensions.rows) {
            // Horizontal axis on bottom border: only "negative" sector exists.
            sector = "negative";
          } else {
            // Normal case: determine sector by position.
            sector = start.ny < axisNY ? "negative" : "positive";
          }
          window.requestAnimationFrame(() => {
            onAxisMove({ kind: "axis-swap", axis: "vertical", sector });
          });
        }
      } else if (isVerticalSwipe && distToHorizontal <= maxDistanceFromAxis) {
        // Vertical swipe near horizontal axis → swap across horizontal axis
        // Check if horizontal axis is not on top/bottom border.
        if (axes.horizontalIndex > 0 && axes.horizontalIndex < dimensions.rows) {
          let sector: AxisSector;
          if (axes.verticalIndex === 0) {
            // Vertical axis on left border: only "positive" sector exists.
            sector = "positive";
          } else if (axes.verticalIndex === dimensions.cols) {
            // Vertical axis on right border: only "negative" sector exists.
            sector = "negative";
          } else {
            // Normal case: determine sector by position.
            sector = start.nx < axisNX ? "negative" : "positive";
          }
          window.requestAnimationFrame(() => {
            onAxisMove({ kind: "axis-swap", axis: "horizontal", sector });
          });
        }
      }
    };

    window.addEventListener("pointerup", handleUp, { passive: true });
    window.addEventListener("pointercancel", handleUp, { passive: true });
    return () => {
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [axes.verticalIndex, axes.horizontalIndex, dimensions.cols, dimensions.rows, onAxisMove]);

  const renderGrid = (sourceGrid: number[][]) => (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 420,
        marginInline: "auto"
      }}
    >
      <div
        style={{
          position: "relative",
          borderRadius: 16,
          padding: 4,
          background: "linear-gradient(135deg, #e5e7eb, #9ca3af, #111827)"
        }}
      >
        <div
          style={{
            backgroundColor: "#020617",
            borderRadius: 12,
            padding: 4
          }}
          ref={boardRef}
          onPointerDown={(e) => {
            // Ignore pointer-downs that start on the axis handle itself.
            const target = e.target as HTMLElement;
            if (target.dataset.role === "axis-handle") return;
            const pos = positionInBoard(e.clientX, e.clientY);
            if (!pos) return;
            swipeStartRef.current = {
              x: e.clientX,
              y: e.clientY,
              nx: pos.ratioX,
              ny: pos.ratioY
            };
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${dimensions.cols}, 1fr)`,
              gap: 2
            }}
          >
            {sourceGrid.map((row, r) =>
              row.map((tileId, c) => {
                const solvedRow = Math.floor(tileId / dimensions.cols);
                const solvedCol = tileId % dimensions.cols;
                const bgPosX = (solvedCol / (dimensions.cols - 1 || 1)) * 100;
                const bgPosY = (solvedRow / (dimensions.rows - 1 || 1)) * 100;
                return (
                  <div
                    key={`${r}-${c}`}
                    style={{
                      ...pieceStyle,
                      backgroundPosition: `${bgPosX}% ${bgPosY}%`
                    }}
                  />
                );
              })
            )}
          </div>
        </div>

            {!isShowingSolution && !completedAt && (
              <>
                {/* Vertical axis line */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                insetBlock: 10,
                left: `calc(${(axes.verticalIndex / dimensions.cols) * 100}% - 2px)`,
                width: 4,
                borderRadius: 999,
                background:
                  "linear-gradient(to bottom, rgba(94, 234, 212, 0.2), rgba(37, 99, 235, 0.7))",
                boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.9)"
              }}
            />

            {/* Horizontal axis line */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                insetInline: 10,
                top: `calc(${(axes.horizontalIndex / dimensions.rows) * 100}% - 2px)`,
                height: 4,
                borderRadius: 999,
                background:
                  "linear-gradient(to right, rgba(94, 234, 212, 0.2), rgba(37, 99, 235, 0.7))",
                boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.9)"
              }}
            />

                {/* Draggable intersection handle */}
                <button
                  type="button"
                  aria-label="Drag to move both axes"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    (e.target as HTMLElement).setPointerCapture(e.pointerId);
                    setIsDraggingAxes(true);
                    updateAxesFromPointer(e.clientX, e.clientY);
                  }}
                  style={{
                    position: "absolute",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "2px solid rgba(94, 234, 212, 0.9)",
                    background: "radial-gradient(circle at 30% 30%, #22d3ee, #2563eb)",
                    boxShadow: "0 10px 20px rgba(37, 99, 235, 0.35)",
                    left: `calc(${(axes.verticalIndex / dimensions.cols) * 100}% - 12px)`,
                    top: `calc(${(axes.horizontalIndex / dimensions.rows) * 100}% - 12px)`,
                    cursor: "grab",
                    touchAction: "none"
                  }}
                  data-role="axis-handle"
                />
          </>
        )}
      </div>
    </div>
  );

  return renderGrid(isShowingSolution ? solvedGrid : grid);
};

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

